import { useState } from 'react'

import { DataTable, type Column } from '@/components/data/DataTable'
import { DetailPane, DetailSection, KeyValue, detailActionClass } from '@/components/data/DetailPane'
import { LoadingRows } from '@/components/data/SearchInput'
import { SplitView } from '@/components/data/SplitView'
import { useSelection } from '@/lib/useSelection'

import { AddField, SectionError, SectionStrip, TOKEN_HINT } from './SectionLabel'
import type { DialogState } from './types'

export type Version = {
  id: string
  name: string
  released: boolean
  archived: boolean
  release_date: string | null
}
export type Component = { id: string; name: string }

const versionState = (v: Version) => (v.archived ? 'archived' : v.released ? 'released' : 'unreleased')

const VERSION_COLUMNS: Column<Version>[] = [
  { key: 'name', header: 'Version' },
  {
    key: 'state', header: 'State', width: '18%', title: versionState,
    render: (v) => <span className={v.released && !v.archived ? 'text-ok' : 'text-text-2'}>{versionState(v)}</span>,
  },
  { key: 'release_date', header: 'Release date', mono: true, width: '22%', render: (v) => v.release_date ?? '—' },
]

type SectionProps<T> = {
  projectKey: string
  data: T[] | null
  err: boolean
  refresh: () => void
  onDialog: (d: DialogState) => void
}

function VersionDetail({ version: v, others, onDialog, refresh }: {
  version: Version; others: Version[]; onDialog: (d: DialogState) => void; refresh: () => void
}) {
  const [target, setTarget] = useState('')
  return (
    <DetailPane
      eyebrow="Version"
      title={v.name}
      status={<span className={v.released && !v.archived ? 'text-ok' : 'text-text-2'}>{versionState(v)}</span>}
      actions={!v.released && (
        <button
          type="button"
          className={detailActionClass}
          onClick={() => onDialog({
            op: 'projects.release_version',
            params: { version_id: v.id },
            title: `Release ${v.name}`,
            onDone: refresh,
          })}
        >
          Release
        </button>
      )}
    >
      <KeyValue
        items={[
          ['Release date', <span className="font-mono">{v.release_date ?? '—'}</span>],
          ['Version id', <span className="font-mono text-[12px] text-text-2">{v.id}</span>],
        ]}
      />
      {others.length > 0 && (
        <DetailSection title="Merge into another version">
          <div className="flex items-center gap-2">
            <select
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              aria-label={`Merge ${v.name} into`}
              className="h-7 min-w-0 flex-1 rounded-md border border-input bg-surface px-2 font-mono text-[12px] text-foreground"
            >
              <option value="">Choose a version…</option>
              {others.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
            <button
              type="button"
              disabled={!target}
              className={detailActionClass}
              onClick={() => onDialog({
                op: 'projects.merge_versions',
                params: { source_version_id: v.id, target_version_id: target },
                title: `Merge ${v.name}`,
                onDone: refresh,
              })}
            >
              Merge
            </button>
          </div>
        </DetailSection>
      )}
    </DetailPane>
  )
}

export function VersionsSection({ projectKey, data, err, refresh, onDialog }: SectionProps<Version>) {
  const [newName, setNewName] = useState('')
  const [sel, setSel] = useSelection()
  const create = () => {
    if (!newName.trim()) return
    onDialog({
      op: 'projects.create_version',
      params: { project_key: projectKey, name: newName.trim() },
      title: 'Create version',
      onDone: () => {
        setNewName('')
        refresh()
      },
    })
  }
  const selected = data?.find((v) => v.id === sel) ?? null
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <SectionStrip label="versions" onRefresh={refresh}>
        <AddField value={newName} onChange={setNewName} onAdd={create} placeholder="New version name" label="New version name" />
      </SectionStrip>
      {err ? <SectionError>Couldn’t load versions — {TOKEN_HINT}</SectionError>
        : data === null ? <LoadingRows label="Loading versions" rows={4} />
        : (
          <SplitView
            onCloseDetail={() => setSel(null)}
            detail={selected && (
              <VersionDetail
                key={selected.id}
                version={selected}
                others={data.filter((o) => o.id !== selected.id)}
                onDialog={onDialog}
                refresh={refresh}
              />
            )}
            list={(
              <DataTable label="Versions" rows={data} columns={VERSION_COLUMNS} rowId={(v) => v.id}
                selectedId={sel} onSelect={setSel} empty="No versions." />
            )}
          />
        )}
    </div>
  )
}

const COMPONENT_COLUMNS: Column<Component>[] = [
  { key: 'name', header: 'Component' },
  { key: 'id', header: 'Id', mono: true, width: '20%' },
]

export function ComponentsSection({ projectKey, data, err, refresh, onDialog }: SectionProps<Component>) {
  const [newName, setNewName] = useState('')
  const [sel, setSel] = useSelection()
  const create = () => {
    if (!newName.trim()) return
    onDialog({
      op: 'projects.create_component',
      params: { project_key: projectKey, name: newName.trim() },
      title: 'Create component',
      onDone: () => {
        setNewName('')
        refresh()
      },
    })
  }
  const selected = data?.find((c) => c.id === sel) ?? null
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <SectionStrip label="components" onRefresh={refresh}>
        <AddField value={newName} onChange={setNewName} onAdd={create} placeholder="New component name" label="New component name" />
      </SectionStrip>
      {err ? <SectionError>Couldn’t load components — {TOKEN_HINT}</SectionError>
        : data === null ? <LoadingRows label="Loading components" rows={4} />
        : (
          <SplitView
            onCloseDetail={() => setSel(null)}
            detail={selected && (
              <DetailPane eyebrow="Component" title={selected.name}>
                <KeyValue items={[['Component id', <span className="font-mono text-[12px] text-text-2">{selected.id}</span>]]} />
              </DetailPane>
            )}
            list={(
              <DataTable label="Components" rows={data} columns={COMPONENT_COLUMNS} rowId={(c) => c.id}
                selectedId={sel} onSelect={setSel} empty="No components." />
            )}
          />
        )}
    </div>
  )
}
